import React, { useCallback, useEffect, useState } from 'react';
import { authAPI } from '../../services/api';

const fmtErr = (d, fallback) => {
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

const C = {
  navy: '#2B1B22', muted: '#8b7681', border: '#ECE3E7', soft: '#f7f3f4',
  primary: '#7B1E3C', green: '#2BB673', red: '#D64545',
  font: 'Inter, sans-serif',
};

const card = {
  background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14,
  padding: '22px 24px', boxShadow: '0 1px 2px rgba(13,27,46,0.04)',
  fontFamily: C.font,
};

const btn = (bg, ghost) => ({
  padding: '8px 14px', borderRadius: 9, fontSize: 12.5, fontWeight: 700,
  fontFamily: C.font, cursor: 'pointer',
  background: ghost ? '#fff' : bg, color: ghost ? bg : '#fff',
  border: `1px solid ${bg}`,
});

const INVITE_ROLES = ['PATIENT', 'NURSE', 'DOCTOR', 'BILLING', 'QA', 'CAREGIVER', 'ADMIN'];

export default function AccessManagement() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null); // {tone, text}
  const [busy, setBusy] = useState('');

  const [inv, setInv] = useState({ name: '', email: '', phone: '', roles: ['NURSE'] });
  const [invBusy, setInvBusy] = useState(false);
  const [invLink, setInvLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    authAPI.pendingSignups()
      .then(r => setPending(Array.isArray(r) ? r : (r?.data || [])))
      .catch(err => setBanner({ tone: 'err', text: fmtErr(err.response?.data?.detail, 'Could not load the queue.') }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (partyId, action) => {
    setBusy(partyId + action);
    setBanner(null);
    try {
      if (action === 'approve') await authAPI.approveSignup(partyId);
      else await authAPI.rejectSignup(partyId);
      setBanner({ tone: 'ok', text: action === 'approve' ? 'Account approved — the user can sign in now.' : 'Request rejected.' });
      load();
    } catch (err) {
      setBanner({ tone: 'err', text: fmtErr(err.response?.data?.detail, 'Action failed.') });
    } finally {
      setBusy('');
    }
  };

  const toggleRole = (r) => setInv(v => ({
    ...v, roles: v.roles.includes(r) ? v.roles.filter(x => x !== r) : [...v.roles, r],
  }));

  const createInvite = async (e) => {
    e.preventDefault();
    setInvBusy(true);
    setBanner(null);
    setInvLink('');
    try {
      const resp = await authAPI.createInvite({
        name: inv.name, email: inv.email, roles: inv.roles,
        phone: inv.phone || undefined,
      });
      const d = resp?.data ?? resp;
      setInvLink(`${window.location.origin}${d.invite_path}`);
      setInv({ name: '', email: '', phone: '', roles: ['NURSE'] });
      load(); // invited accounts appear in the pending list until accepted
    } catch (err) {
      setBanner({ tone: 'err', text: fmtErr(err.response?.data?.detail, 'Could not create the invite.') });
    } finally {
      setInvBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(invLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const input = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9,
    border: `1px solid ${C.border}`, fontSize: 13, fontFamily: C.font, color: C.navy,
    outline: 'none', background: '#fff',
  };
  const label = {
    display: 'block', fontSize: 10.5, fontWeight: 800, color: C.muted,
    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5,
  };

  return (
    <div style={{ padding: '26px 30px', fontFamily: C.font }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: 0 }}>Access Management</h1>
        <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0' }}>
          Approve patient sign-up requests and invite staff — invitees set their own password,
          so credentials never travel by hand.
        </p>
      </div>

      {banner && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13,
          background: banner.tone === 'ok' ? 'rgba(43,182,115,0.1)' : '#FDECEA',
          color: banner.tone === 'ok' ? '#1d7a4d' : '#B71C1C',
          border: `1px solid ${banner.tone === 'ok' ? 'rgba(43,182,115,0.35)' : '#F5C2C0'}`,
        }}>{banner.text}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 1.4fr) minmax(340px, 1fr)', gap: 20, alignItems: 'start' }}>
        {/* ── Pending queue ── */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: C.navy, margin: 0 }}>
              ⏳ Awaiting activation ({pending.length})
            </h2>
            <button onClick={load} style={btn(C.primary, true)}>Refresh</button>
          </div>

          {loading ? (
            <div style={{ color: C.muted, fontSize: 13, padding: '18px 0' }}>Loading…</div>
          ) : pending.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 13, padding: '18px 0' }}>
              Nothing waiting — new sign-ups and unaccepted invites appear here.
            </div>
          ) : pending.map(p => (
            <div key={p.party_id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
              borderBottom: `1px solid ${C.soft}`,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg,#7B1E3C,#C39A4E)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800,
              }}>
                {(p.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.navy }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                  {(p.roles || []).join(', ') || '—'} · {p.email || p.phone || 'no contact'}
                  {p.requested_at && ` · ${new Date(p.requested_at).toLocaleDateString()}`}
                </div>
              </div>
              <button disabled={busy === p.party_id + 'approve'}
                onClick={() => act(p.party_id, 'approve')} style={btn(C.green)}>
                {busy === p.party_id + 'approve' ? '…' : 'Approve'}
              </button>
              <button disabled={busy === p.party_id + 'reject'}
                onClick={() => act(p.party_id, 'reject')} style={btn(C.red, true)}>
                {busy === p.party_id + 'reject' ? '…' : 'Reject'}
              </button>
            </div>
          ))}
        </div>

        {/* ── Invite form ── */}
        <div style={card}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: C.navy, margin: '0 0 4px' }}>✉️ Invite a user</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 16px', lineHeight: 1.55 }}>
            Creates the account and a one-time link (valid 72 h). The invitee opens it,
            sets their own password, and the account activates.
          </p>

          <form onSubmit={createInvite}>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Full name</label>
              <input style={input} value={inv.name} required
                onChange={e => setInv({ ...inv, name: e.target.value })} placeholder="e.g. Grace Achieng" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={label}>Email</label>
                <input style={input} type="email" value={inv.email} required
                  onChange={e => setInv({ ...inv, email: e.target.value })} placeholder="grace@hhms.health" />
              </div>
              <div>
                <label style={label}>Phone</label>
                <input style={input} value={inv.phone}
                  onChange={e => setInv({ ...inv, phone: e.target.value })} placeholder="optional" />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {INVITE_ROLES.map(r => {
                  const on = inv.roles.includes(r);
                  return (
                    <button type="button" key={r} onClick={() => toggleRole(r)} style={{
                      padding: '6px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                      fontFamily: C.font, cursor: 'pointer',
                      background: on ? C.primary : '#fff',
                      color: on ? '#fff' : C.muted,
                      border: `1px solid ${on ? C.primary : C.border}`,
                    }}>{r}</button>
                  );
                })}
              </div>
            </div>
            <button type="submit" disabled={invBusy || inv.roles.length === 0} style={{
              ...btn(C.primary), width: '100%', padding: '11px 14px', fontSize: 13.5,
              opacity: invBusy || inv.roles.length === 0 ? 0.6 : 1,
            }}>
              {invBusy ? 'Creating…' : 'Create invite link'}
            </button>
          </form>

          {invLink && (
            <div style={{
              marginTop: 16, padding: '12px 14px', borderRadius: 10,
              background: 'rgba(43,182,115,0.07)', border: '1px solid rgba(43,182,115,0.3)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#1d7a4d', marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Invite link — share it with the user
              </div>
              <div style={{
                fontSize: 11, color: C.navy, wordBreak: 'break-all', lineHeight: 1.5,
                fontFamily: 'ui-monospace, Menlo, monospace', marginBottom: 8,
              }}>{invLink}</div>
              <button onClick={copy} style={btn(C.green)}>
                {copied ? 'Copied ✓' : 'Copy link'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
